<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const { t } = useI18n();

const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);

const filters = ref({
  from: 0,
  to: 0,
  userIds: [],
  labelId: null,
  inboxId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});

const summary = ref({
  current_period: {},
  previous_period: {},
  first_response: {
    current_seconds: 0,
    previous_seconds: 0,
  },
  average_response: {
    current_seconds: 0,
    previous_seconds: 0,
  },
});

const series = ref([]);
const isFirstBreakdownOpen = ref(true);
const isAverageBreakdownOpen = ref(true);

const toNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toInt = value => Math.round(toNumber(value));
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const formatDuration = totalSeconds => {
  const seconds = Math.max(Math.round(toNumber(totalSeconds)), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) return `${hours}h ${minutes}min`;
  if (minutes > 0) return `${minutes}min ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

const formatDiffTag = totalSeconds => {
  const seconds = Math.round(toNumber(totalSeconds));
  if (seconds === 0) return '(0s)';
  const sign = seconds > 0 ? '+' : '';
  return `(${sign}${formatDuration(Math.abs(seconds))})`;
};

const formatPeriodLabel = period => {
  if (!period?.from || !period?.to) return '-';
  return `${format(fromUnixTime(period.from), "dd MMM''yy")} - ${format(
    fromUnixTime(period.to),
    "dd MMM''yy"
  )}`;
};

const currentPeriodLabel = computed(() =>
  formatPeriodLabel(summary.value.current_period)
);
const previousPeriodLabel = computed(() =>
  formatPeriodLabel(summary.value.previous_period)
);

const responseRows = computed(() =>
  (series.value || []).map(row => {
    const currentTimestamp = toInt(row.current_timestamp);
    const previousTimestamp = toInt(row.previous_timestamp);

    const currentFirstResponseSeconds = toNumber(
      row.current_first_response_seconds
    );
    const previousFirstResponseSeconds = toNumber(
      row.previous_first_response_seconds
    );
    const currentAverageResponseSeconds = toNumber(
      row.current_average_response_seconds
    );
    const previousAverageResponseSeconds = toNumber(
      row.previous_average_response_seconds
    );

    return {
      id: `${currentTimestamp}-${previousTimestamp}`,
      currentTimestamp,
      previousTimestamp,
      currentDateLabel: format(fromUnixTime(currentTimestamp), "dd MMM''yy"),
      previousDateLabel: format(fromUnixTime(previousTimestamp), "dd MMM''yy"),
      currentShortLabel: format(fromUnixTime(currentTimestamp), 'd MMM'),
      previousShortLabel: format(fromUnixTime(previousTimestamp), 'd MMM'),
      currentFirstResponseSeconds,
      previousFirstResponseSeconds,
      currentAverageResponseSeconds,
      previousAverageResponseSeconds,
      currentFirstResponseCount: toInt(row.current_first_response_count),
      previousFirstResponseCount: toInt(row.previous_first_response_count),
      currentAverageResponseCount: toInt(row.current_average_response_count),
      previousAverageResponseCount: toInt(row.previous_average_response_count),
      firstResponseDiff:
        currentFirstResponseSeconds - previousFirstResponseSeconds,
      averageResponseDiff:
        currentAverageResponseSeconds - previousAverageResponseSeconds,
    };
  })
);

const firstHoveredIndex = ref(null);
const averageHoveredIndex = ref(null);

const withAlpha = (hex, alpha) => {
  const raw = String(hex || '')
    .replace('#', '')
    .trim();
  if (raw.length !== 6) return hex;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const makeBarColors = (baseHex, hoveredIndex, count) => {
  const dimmed = withAlpha(baseHex, 0.25);
  return Array.from({ length: count }, (_, i) =>
    hoveredIndex == null || i === hoveredIndex ? baseHex : dimmed
  );
};

const firstResponseChartCollection = computed(() => {
  const rows = responseRows.value;
  return {
    labels: rows.map(row => [row.currentShortLabel, row.previousShortLabel]),
    datasets: [
      {
        label: currentPeriodLabel.value,
        data: rows.map(row => row.currentFirstResponseSeconds),
        backgroundColor: makeBarColors('#0076f5', null, rows.length),
        borderRadius: 3,
        categoryPercentage: 0.72,
        barPercentage: 0.95,
      },
      {
        label: previousPeriodLabel.value,
        data: rows.map(row => row.previousFirstResponseSeconds),
        backgroundColor: makeBarColors('#eecc1b', null, rows.length),
        borderRadius: 3,
        categoryPercentage: 0.72,
        barPercentage: 0.95,
      },
    ],
  };
});

const averageResponseChartCollection = computed(() => {
  const rows = responseRows.value;
  return {
    labels: rows.map(row => [row.currentShortLabel, row.previousShortLabel]),
    datasets: [
      {
        label: currentPeriodLabel.value,
        data: rows.map(row => row.currentAverageResponseSeconds),
        backgroundColor: makeBarColors('#0076f5', null, rows.length),
        borderRadius: 3,
        categoryPercentage: 0.72,
        barPercentage: 0.95,
      },
      {
        label: previousPeriodLabel.value,
        data: rows.map(row => row.previousAverageResponseSeconds),
        backgroundColor: makeBarColors('#eecc1b', null, rows.length),
        borderRadius: 3,
        categoryPercentage: 0.72,
        barPercentage: 0.95,
      },
    ],
  };
});

const ensureTooltipEl = id => {
  const existing = document.getElementById(id);
  if (existing) return existing;
  const el = document.createElement('div');
  el.id = id;
  el.className = 'crt-ext-tooltip';
  el.style.cssText =
    'opacity:0;position:fixed;pointer-events:auto;transform:translate(-50%,-100%);transition:opacity 120ms ease;z-index:9999;';
  document.body.appendChild(el);
  return el;
};

const applyBarDimming = (chart, hoveredIndex) => {
  const labelsCount = Array.isArray(chart?.data?.labels)
    ? chart.data.labels.length
    : 0;
  if (!labelsCount || !Array.isArray(chart?.data?.datasets)) return;

  const baseColors = ['#0076f5', '#eecc1b'];
  chart.data.datasets.forEach((dataset, datasetIndex) => {
    const base = baseColors[datasetIndex] || baseColors[0];
    dataset.backgroundColor = makeBarColors(base, hoveredIndex, labelsCount);
  });
  chart.update('none');
};

const removeTooltipEl = id => {
  const el = document.getElementById(id);
  if (el?.parentNode) el.parentNode.removeChild(el);
};

const externalTooltipHandler =
  ({ tooltipId, metric }) =>
  context => {
    const { chart, tooltip } = context;
    const el = ensureTooltipEl(tooltipId);

    if (!tooltip || tooltip.opacity === 0) {
      el.style.opacity = '0';
      return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    const dataIndex = dataPoint?.dataIndex;
    const datasetIndex = dataPoint?.datasetIndex;
    const row = responseRows.value[dataIndex];
    if (!row) return;

    const title =
      datasetIndex === 1 ? row.previousDateLabel : row.currentDateLabel;
    const valueSeconds = Number(dataPoint?.raw || 0);

    let count = 0;
    if (metric === 'first') {
      count =
        datasetIndex === 1
          ? row.previousFirstResponseCount
          : row.currentFirstResponseCount;
    } else {
      count =
        datasetIndex === 1
          ? row.previousAverageResponseCount
          : row.currentAverageResponseCount;
    }

    const metricLabel =
      metric === 'first' ? 'Avg. first response time' : 'Avg. response time';

    el.innerHTML = `
    <div class="crt-ext-tooltip-inner" style="min-width:220px;border:1px solid #d1d5db;background:#fff;border-radius:4px;box-shadow:rgba(0,0,0,0.2) 0 5px 10px 0;overflow:hidden;">
      <div class="crt-ext-tooltip-header" style="background:#eff3f5;font-size:13px;line-height:20px;color:#111827;padding:4px 10px;text-align:center;">${title}</div>
      <div class="crt-ext-tooltip-body" style="font-size:13px;line-height:20px;color:#424d57;padding:6px 12px;background:#fff;">
        <div>${metricLabel}: <strong style="font-weight:600;color:#111827;">${formatDuration(valueSeconds)}</strong></div>
        <div>Number of chats: <strong style="font-weight:600;color:#111827;">${count}</strong></div>
      </div>
    </div>
  `;

    const rect = chart.canvas.getBoundingClientRect();
    const tooltipWidth = el.offsetWidth || 220;
    const tooltipHeight = el.offsetHeight || 110;
    const left = clamp(
      rect.left + tooltip.caretX,
      tooltipWidth / 2 + 8,
      window.innerWidth - tooltipWidth / 2 - 8
    );
    const top = Math.max(rect.top + tooltip.caretY - 10, tooltipHeight + 8);

    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.opacity = '1';
  };

const buildChartOptions = ({ metric, hoveredRef, tooltipId }) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: {
    mode: 'nearest',
    intersect: true,
  },
  onHover: (_event, active, chart) => {
    const hoveredIndex = active?.length ? active[0].index : null;
    if (hoveredRef.value === hoveredIndex) return;
    hoveredRef.value = hoveredIndex;
    applyBarDimming(chart, hoveredIndex);
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
      displayColors: false,
      backgroundColor: '#ffffff',
      titleColor: '#111827',
      bodyColor: '#4b5563',
      borderColor: '#d1d5db',
      borderWidth: 1,
      padding: 10,
      external: externalTooltipHandler({ tooltipId, metric }),
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: '#6b7280',
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: '#e5e7eb',
      },
      ticks: {
        color: '#6b7280',
        callback(value) {
          return `${Math.round(Number(value))} s`;
        },
      },
    },
  },
});

const firstChartOptions = computed(() =>
  buildChartOptions({
    metric: 'first',
    hoveredRef: firstHoveredIndex,
    tooltipId: 'crt-tooltip-first',
  })
);
const averageChartOptions = computed(() =>
  buildChartOptions({
    metric: 'average',
    hoveredRef: averageHoveredIndex,
    tooltipId: 'crt-tooltip-average',
  })
);

const diffClass = value => {
  if (value > 0) return 'crt-diff-up';
  if (value < 0) return 'crt-diff-down';
  return 'crt-diff-neutral';
};

const toggleFirstBreakdown = () => {
  isFirstBreakdownOpen.value = !isFirstBreakdownOpen.value;
};

const toggleAverageBreakdown = () => {
  isAverageBreakdownOpen.value = !isAverageBreakdownOpen.value;
};

const fetchReport = async () => {
  if (!filters.value.from || !filters.value.to) return;

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getChatResponseTimes({
      from: filters.value.from,
      to: filters.value.to,
      userIds: filters.value.userIds,
      labelId: filters.value.labelId,
      inboxId: filters.value.inboxId,
      teamId: filters.value.teamId,
      vipLevelId: filters.value.vipLevelId,
      issueId: filters.value.issueId,
      priority: filters.value.priority,
    });

    summary.value = response.data?.summary || summary.value;
    series.value = response.data?.series || [];
    generatedAt.value = new Date();
  } catch {
    errorMessage.value =
      t('CHAT_RESPONSE_TIMES_REPORT.FETCH_ERROR') ||
      'Unable to load chat response times report.';
    series.value = [];
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({
  from,
  to,
  selectedAgents = [],
  selectedLabel = null,
  selectedInbox = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
}) => {
  filters.value = {
    from,
    to,
    userIds: Array.isArray(selectedAgents)
      ? selectedAgents.map(agent => agent.id)
      : [],
    labelId: selectedLabel?.id || null,
    inboxId: selectedInbox?.id || null,
    teamId: selectedTeam?.id || null,
    vipLevelId: selectedVip?.id || null,
    issueId: selectedIssue?.id || null,
    priority: selectedPriority?.id || null,
  };
  fetchReport();
};

const downloadFirstResponseCsv = () => {
  const headers = [
    'Series',
    ...responseRows.value.map(
      row => `${row.currentDateLabel} vs ${row.previousDateLabel}`
    ),
  ];

  const rows = [
    [
      'First response time',
      ...responseRows.value.map(
        row =>
          `${formatDuration(row.currentFirstResponseSeconds)} | ${formatDuration(
            row.previousFirstResponseSeconds
          )} | ${formatDiffTag(row.firstResponseDiff)}`
      ),
    ],
  ];

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'chat-response-times-first-response-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadAverageResponseCsv = () => {
  const headers = [
    'Series',
    ...responseRows.value.map(
      row => `${row.currentDateLabel} vs ${row.previousDateLabel}`
    ),
  ];

  const rows = [
    [
      'Average response time',
      ...responseRows.value.map(
        row =>
          `${formatDuration(row.currentAverageResponseSeconds)} | ${formatDuration(
            row.previousAverageResponseSeconds
          )} | ${formatDiffTag(row.averageResponseDiff)}`
      ),
    ],
  ];

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'chat-response-times-average-response-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const generatedAtLabel = computed(() =>
  generatedAt.value
    ? format(generatedAt.value, "dd MMM yyyy 'at' hh:mm:ss a")
    : '-'
);

onBeforeUnmount(() => {
  removeTooltipEl('crt-tooltip-first');
  removeTooltipEl('crt-tooltip-average');
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="crt-page">
    <div class="crt-shell">
      <div class="crt-header">
        <div class="crt-header-left">
          <h1 class="crt-title">Chat response times</h1>
        </div>
      </div>

      <div class="crt-toolbar">
        <div class="crt-toolbar-left">
          <CompactDateInboxFilters
            :allowed-date-range-ids="SUPPORTED_RANGE_IDS"
            show-agents
            show-labels
            show-teams
            show-vip
            show-issue
            show-priority
            hide-inactive-filters
            @filter-change="onFilterChange"
          />
        </div>
      </div>

      <div class="crt-content">
        <div v-if="errorMessage" class="crt-error">
          {{ errorMessage }}
        </div>

        <section class="crt-card">
          <div class="crt-card-header">
            <div class="crt-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <span>First response time</span>
            </div>
          </div>

          <div class="crt-metric-row">
            <div class="crt-metric-item">
              <div class="crt-metric-label">
                <span class="crt-dot crt-dot-blue" />
                <span>{{ currentPeriodLabel }}</span>
              </div>
              <div class="crt-metric-value">
                {{ formatDuration(summary.first_response?.current_seconds) }}
              </div>
            </div>
            <div class="crt-metric-item">
              <div class="crt-metric-label">
                <span class="crt-dot crt-dot-yellow" />
                <span>{{ previousPeriodLabel }}</span>
              </div>
              <div class="crt-metric-value">
                {{ formatDuration(summary.first_response?.previous_seconds) }}
              </div>
            </div>
          </div>

          <div class="crt-chart">
            <woot-loading-state
              v-if="isLoading"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!responseRows.length" class="crt-empty">
              {{ t('CHAT_RESPONSE_TIMES_REPORT.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="firstResponseChartCollection"
              :chart-options="firstChartOptions"
            />
          </div>

          <div class="crt-breakdown">
            <div
              class="crt-breakdown-header"
              role="button"
              tabindex="0"
              @click="toggleFirstBreakdown"
              @keydown.enter.prevent="toggleFirstBreakdown"
              @keydown.space.prevent="toggleFirstBreakdown"
            >
              <div class="crt-breakdown-title">
                <svg
                  class="crt-breakdown-caret"
                  :class="{ 'is-collapsed': !isFirstBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>First response time breakdown</span>
              </div>

              <button
                class="crt-export-btn"
                type="button"
                @click.stop="downloadFirstResponseCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>Export CSV</span>
              </button>
            </div>

            <div v-show="isFirstBreakdownOpen" class="crt-table-wrap">
              <table class="crt-table">
                <thead>
                  <tr>
                    <th class="crt-th-series">Series</th>
                    <template
                      v-for="(row, index) in responseRows"
                      :key="`first-h-${row.id}`"
                    >
                      <th
                        class="crt-th-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-head-tone-odd'
                            : 'crt-head-tone-even'
                        "
                      >
                        {{ row.currentDateLabel }}
                      </th>
                      <th
                        class="crt-th-vs"
                        :class="
                          index % 2 === 0
                            ? 'crt-head-tone-odd'
                            : 'crt-head-tone-even'
                        "
                      >
                        vs
                      </th>
                      <th
                        class="crt-th-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-head-tone-odd'
                            : 'crt-head-tone-even'
                        "
                      >
                        {{ row.previousDateLabel }}
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="crt-td-series">First response time</td>
                    <template
                      v-for="(row, index) in responseRows"
                      :key="`first-r-${row.id}`"
                    >
                      <td
                        class="crt-td-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-body-tone-odd'
                            : 'crt-body-tone-even'
                        "
                      >
                        <span class="crt-cell-main">
                          {{ formatDuration(row.currentFirstResponseSeconds) }}
                        </span>
                        <span
                          class="crt-cell-diff"
                          :class="diffClass(row.firstResponseDiff)"
                        >
                          {{ formatDiffTag(row.firstResponseDiff) }}
                        </span>
                      </td>
                      <td
                        class="crt-td-vs"
                        :class="
                          index % 2 === 0
                            ? 'crt-body-tone-odd'
                            : 'crt-body-tone-even'
                        "
                      />
                      <td
                        class="crt-td-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-body-tone-odd'
                            : 'crt-body-tone-even'
                        "
                      >
                        <span class="crt-cell-main">
                          {{ formatDuration(row.previousFirstResponseSeconds) }}
                        </span>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="crt-card">
          <div class="crt-card-header">
            <div class="crt-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <span>Average response time</span>
            </div>
          </div>

          <div class="crt-metric-row">
            <div class="crt-metric-item">
              <div class="crt-metric-label">
                <span class="crt-dot crt-dot-blue" />
                <span>{{ currentPeriodLabel }}</span>
              </div>
              <div class="crt-metric-value">
                {{ formatDuration(summary.average_response?.current_seconds) }}
              </div>
            </div>
            <div class="crt-metric-item">
              <div class="crt-metric-label">
                <span class="crt-dot crt-dot-yellow" />
                <span>{{ previousPeriodLabel }}</span>
              </div>
              <div class="crt-metric-value">
                {{ formatDuration(summary.average_response?.previous_seconds) }}
              </div>
            </div>
          </div>

          <div class="crt-chart">
            <woot-loading-state
              v-if="isLoading"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!responseRows.length" class="crt-empty">
              {{ t('CHAT_RESPONSE_TIMES_REPORT.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="averageResponseChartCollection"
              :chart-options="averageChartOptions"
            />
          </div>

          <div class="crt-breakdown">
            <div
              class="crt-breakdown-header"
              role="button"
              tabindex="0"
              @click="toggleAverageBreakdown"
              @keydown.enter.prevent="toggleAverageBreakdown"
              @keydown.space.prevent="toggleAverageBreakdown"
            >
              <div class="crt-breakdown-title">
                <svg
                  class="crt-breakdown-caret"
                  :class="{ 'is-collapsed': !isAverageBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>Average response time breakdown</span>
              </div>

              <button
                class="crt-export-btn"
                type="button"
                @click.stop="downloadAverageResponseCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>Export CSV</span>
              </button>
            </div>

            <div v-show="isAverageBreakdownOpen" class="crt-table-wrap">
              <table class="crt-table">
                <thead>
                  <tr>
                    <th class="crt-th-series">Series</th>
                    <template
                      v-for="(row, index) in responseRows"
                      :key="`avg-h-${row.id}`"
                    >
                      <th
                        class="crt-th-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-head-tone-odd'
                            : 'crt-head-tone-even'
                        "
                      >
                        {{ row.currentDateLabel }}
                      </th>
                      <th
                        class="crt-th-vs"
                        :class="
                          index % 2 === 0
                            ? 'crt-head-tone-odd'
                            : 'crt-head-tone-even'
                        "
                      >
                        vs
                      </th>
                      <th
                        class="crt-th-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-head-tone-odd'
                            : 'crt-head-tone-even'
                        "
                      >
                        {{ row.previousDateLabel }}
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="crt-td-series">Avg. response time</td>
                    <template
                      v-for="(row, index) in responseRows"
                      :key="`avg-r-${row.id}`"
                    >
                      <td
                        class="crt-td-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-body-tone-odd'
                            : 'crt-body-tone-even'
                        "
                      >
                        <span class="crt-cell-main">
                          {{
                            formatDuration(row.currentAverageResponseSeconds)
                          }}
                        </span>
                        <span
                          class="crt-cell-diff"
                          :class="diffClass(row.averageResponseDiff)"
                        >
                          {{ formatDiffTag(row.averageResponseDiff) }}
                        </span>
                      </td>
                      <td
                        class="crt-td-vs"
                        :class="
                          index % 2 === 0
                            ? 'crt-body-tone-odd'
                            : 'crt-body-tone-even'
                        "
                      />
                      <td
                        class="crt-td-data"
                        :class="
                          index % 2 === 0
                            ? 'crt-body-tone-odd'
                            : 'crt-body-tone-even'
                        "
                      >
                        <span class="crt-cell-main">
                          {{
                            formatDuration(row.previousAverageResponseSeconds)
                          }}
                        </span>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="crt-footer-note">
          This report was generated on {{ generatedAtLabel }}<br />
          All reports are displayed in your local time.<br />
          Use the
          <a
            href="https://platform.text.com/docs/data-reporting/reports-api#response-time"
            target="_blank"
            rel="noopener noreferrer"
          >
            Reports API
          </a>
          to access this data remotely.
        </p>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.crt-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.crt-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.crt-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.crt-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.crt-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.crt-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 3px 10px -6px;
}

.crt-toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.crt-filter-anchor {
  position: relative;
}

.crt-secondary-btn {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  cursor: pointer;
}

.crt-badge-small {
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 10px;
  background: #2563eb;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.crt-date-nav {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 34px;
  padding: 2px;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  background: #f3f4f6;
}

.crt-nav-btn {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.crt-nav-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.crt-date-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.crt-date-chip:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
}

.crt-chip-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #111827;
  font-size: 11px;
  font-weight: 700;
}

.crt-date-menu,
.crt-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 240px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 20px 28px rgba(17, 24, 39, 0.14);
  z-index: 30;
  padding: 8px;
}

.crt-date-menu-title,
.crt-popover-title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}

.crt-date-item {
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #111827;
  min-height: 34px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.crt-date-item:hover,
.crt-date-item.is-active {
  background: #f1f5f9;
}

.crt-filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.crt-filter-field span {
  font-size: 12px;
  color: #4b5563;
}

.crt-filter-field select {
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  padding: 0 8px;
}

.crt-filter-empty {
  min-height: 34px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 13px;
  padding: 7px 10px;
}

.crt-clear-link {
  margin-top: 4px;
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
}

.crt-content {
  padding: 16px 32px 16px 32px;
}

.crt-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.crt-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}

.crt-card-header {
  min-height: 68px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.crt-card-title {
  color: #111827;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.crt-metric-row {
  display: flex;
  flex-wrap: wrap;
  padding: 0 16px;
}

.crt-metric-item {
  overflow: hidden;
  padding-right: 16px;
  margin-bottom: 16px;
  margin-left: -8px;
  position: relative;
  box-sizing: border-box;
}

.crt-metric-item:last-child {
  padding-right: 0;
}

.crt-metric-label {
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 22px;
  color: #111827;
  padding: 10px 16px 2px;
}

.crt-dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 6px;
  flex: 0 0 auto;
}

.crt-dot-blue {
  background: #0076f5;
}

.crt-dot-yellow {
  background: #eecc1b;
}

.crt-metric-value {
  display: flex;
  align-items: center;
  color: #111827;
  font-size: 18px;
  line-height: 28px;
  font-weight: 600;
  min-width: 160px;
  margin-left: 16px;
  white-space: nowrap;
}

.crt-chart {
  position: relative;
  height: 250px;
  margin: 0 16px 8px;
}

.crt-chart :deep(.highcharts-container),
.crt-chart :deep(canvas) {
  overflow: visible !important;
}

:global(.crt-ext-tooltip) {
  z-index: 9999;
}

.crt-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.crt-breakdown {
  border-top: 1px solid #dfe3e8;
}

.crt-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}

.crt-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.crt-breakdown-caret {
  transition: transform 0.18s ease;
}

.crt-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.crt-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
}

.crt-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}

.crt-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  color: #424d57;
}

.crt-table th {
  color: #424d57 !important;
}

.crt-th-series,
.crt-td-series {
  position: sticky;
  left: 0;
  overflow: visible;
  min-width: 160px;
  max-width: 160px;
  text-align: left;
  border-right: 1px solid #d7dde4;
}

.crt-th-series::after,
.crt-td-series::after {
  content: '';
  position: absolute;
  top: 0;
  right: -5px;
  width: 5px;
  height: 100%;
  background: linear-gradient(
    to left,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.05) 100%
  );
  pointer-events: none;
}

.crt-th-series {
  z-index: 3;
  padding: 8px 16px;
  font-weight: 400;
  background: #eff3f5;
}

.crt-td-series {
  z-index: 2;
  padding: 15px 8px;
  background: #ffffff;
  font-weight: 500;
}

.crt-th-data {
  min-width: 100px;
  text-align: center;
  padding: 8px 20px;
  font-weight: 400;
}

.crt-th-vs {
  min-width: 20px;
  max-width: 20px;
  text-align: center;
  color: rgba(66, 77, 87, 0.5);
  padding: 8px 0;
}

.crt-td-data {
  min-width: 100px;
  text-align: center;
  padding: 8px 16px;
  vertical-align: top;
}

.crt-td-vs {
  min-width: 20px;
  max-width: 20px;
  padding: 8px 0;
}

.crt-head-tone-odd {
  background: #eaeef1;
}

.crt-head-tone-even {
  background: #eff3f5;
}

.crt-body-tone-odd {
  background: #f6f7f8;
}

.crt-body-tone-even {
  background: #fefefe;
}

.crt-cell-main {
  display: block;
  color: #111827;
}

.crt-cell-diff {
  display: inline-block;
  font-size: 8px;
}

.crt-diff-up {
  color: #d94848;
}

.crt-diff-down {
  color: #0f8a52;
}

.crt-diff-neutral {
  color: #6b7280;
}

.crt-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.crt-footer-note a {
  color: #2563eb;
  text-decoration: none;
}

:global(body.dark) .crt-page,
:global(body.dark) .crt-shell,
:global(body.dark) .crt-header,
:global(body.dark) .crt-toolbar,
:global(body.dark) .crt-content,
:global(body.dark) .crt-card,
:global(body.dark) .crt-table,
:global(body.dark) .crt-th-series,
:global(body.dark) .crt-td-series,
:global(body.theme-mix) .crt-page,
:global(body.theme-mix) .crt-shell,
:global(body.theme-mix) .crt-header,
:global(body.theme-mix) .crt-toolbar,
:global(body.theme-mix) .crt-content,
:global(body.theme-mix) .crt-card,
:global(body.theme-mix) .crt-table,
:global(body.theme-mix) .crt-th-series,
:global(body.theme-mix) .crt-td-series {
  background: #ffffff !important;
  color: #111827 !important;
}

:global(body.dark) .crt-head-tone-odd {
  background: #eaeef1 !important;
}

:global(body.dark) .crt-head-tone-even {
  background: #eff3f5 !important;
}

:global(body.dark) .crt-body-tone-odd {
  background: #f6f7f8 !important;
}

:global(body.dark) .crt-body-tone-even {
  background: #fefefe !important;
}

:global(body.dark) .crt-table th,
:global(body.dark) .crt-table td,
:global(body.theme-mix) .crt-table th,
:global(body.theme-mix) .crt-table td {
  color: #111827 !important;
  border-color: #e5e7eb !important;
}

@media (max-width: 1024px) {
  .crt-content {
    padding: 16px;
  }

  .crt-card-title {
    font-size: 18px;
    line-height: 24px;
  }

  .crt-metric-value {
    font-size: 18px;
    line-height: 28px;
  }
}

@media (max-width: 768px) {
  .crt-title {
    font-size: 18px !important;
    line-height: 24px;
  }

  .crt-toolbar {
    padding: 10px 12px;
    flex-wrap: wrap;
  }

  .crt-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 10px 12px;
  }
}
</style>
